import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { collection, getDocs, onSnapshot } from 'firebase/firestore';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMoneyCheckDollar } from '@fortawesome/free-solid-svg-icons';
import { useNavigate } from 'react-router-dom';
import Form from 'react-bootstrap/Form';
import Spinner from 'react-bootstrap/Spinner';
import swal from 'sweetalert';
import { db } from '../../firebase';
import '../dash.css';
import {
  buildPayrollRows,
  formatDateTime,
  formatKes,
  getMonthKey,
  getMonthLabel
} from '../../utils/financeWorkflow';

const normalizeEmployeeStatus = (value) => String(value || 'Active').trim();

function PayrollLedgerComp() {
  const navigate = useNavigate();
  const [employees, setEmployees] = useState([]);
  const [payments, setPayments] = useState([]);
  const [periodKey, setPeriodKey] = useState(getMonthKey());
  const [statusFilter, setStatusFilter] = useState('Active');
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);

  const fetchPage = useCallback(async () => {
    setLoading(true);
    try {
      const [employeeSnapshot, paymentSnapshot] = await Promise.all([
        getDocs(collection(db, 'Employees')),
        getDocs(collection(db, 'SalaryPayments'))
      ]);

      setEmployees(employeeSnapshot.docs.map((item) => ({ id: item.id, ...item.data() })));
      setPayments(paymentSnapshot.docs.map((item) => ({ id: item.id, ...item.data() })));
    } catch (error) {
      console.error('Failed to load payroll ledger:', error);
      swal('Error', 'Payroll ledger could not be loaded right now.', 'error');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPage();
  }, [fetchPage]);

  useEffect(() => {
    const unsubscribe = onSnapshot(
      collection(db, 'SalaryPayments'),
      (snapshot) => {
        setPayments(snapshot.docs.map((item) => ({ id: item.id, ...item.data() })));
        setLoading(false);
      },
      (error) => {
        console.error('Failed to subscribe to payroll ledger payments:', error);
      }
    );

    return unsubscribe;
  }, []);

  const payrollRows = useMemo(() => buildPayrollRows({
    employees,
    payments,
    periodKey
  }), [employees, payments, periodKey]);

  const filteredRows = useMemo(() => {
    const normalizedSearch = searchTerm.trim().toLowerCase();

    return payrollRows.filter((employee) => {
      const employeeStatus = normalizeEmployeeStatus(employee.employeeStatus);
      const matchesStatus = statusFilter === 'All' || employeeStatus === statusFilter;
      if (!matchesStatus) {
        return false;
      }

      if (!normalizedSearch) {
        return true;
      }

      const searchBlob = [
        employee.employeeName,
        employee.jobPosition,
        employee.campus,
        employee.employeeEmail,
        employee.employeePhone
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();

      return searchBlob.includes(normalizedSearch);
    });
  }, [payrollRows, searchTerm, statusFilter]);

  const payrollSummary = useMemo(() => {
    return payrollRows.reduce(
      (accumulator, employee) => {
        if (normalizeEmployeeStatus(employee.employeeStatus) === 'Archived') {
          return accumulator;
        }

        accumulator.expected += employee.currentBucket.expectedAmount;
        accumulator.paid += employee.currentBucket.paidAmount;
        accumulator.remaining += employee.currentBucket.remainingAmount;
        accumulator.previousOutstanding += employee.previousOutstanding;
        accumulator.totalOutstanding += employee.totalOutstanding;

        if (employee.totalOutstanding > 0) {
          accumulator.employeesNeedingAction += 1;
        }

        return accumulator;
      },
      {
        expected: 0,
        paid: 0,
        remaining: 0,
        previousOutstanding: 0,
        totalOutstanding: 0,
        employeesNeedingAction: 0
      }
    );
  }, [payrollRows]);

  return (
    <div className="clientsComp incomePage paySalariesPage payrollLedgerPage">
      <section className="incomeHero paySalariesHero">
        <div className="incomeHeroCopy">
          <div className="incomeHeroTitleRow">
            <h5>Payroll Ledger</h5>
          </div>
          <p className="paySalariesHeroText">
            Review payroll balances by month and open the employee profile from any balance row.
          </p>
          <p className="incomeHeaderMeta">
            <span>{getMonthLabel(periodKey)}</span>
            <span className="incomeInlineDot" />
            <span>{payrollSummary.employeesNeedingAction} employees need follow-up</span>
            <span className="incomeInlineDot" />
            <span>{formatKes(payrollSummary.totalOutstanding)} still due</span>
          </p>
        </div>
        <div className="incomeHeroActions paySalariesHeroActions">
          <button
            type="button"
            className="btn btn-dark"
            onClick={() => navigate('/pay-salaries')}
          >
            <FontAwesomeIcon icon={faMoneyCheckDollar} /> Go to pay salaries
          </button>
        </div>
      </section>

      {loading ? (
        <div className="incomeStateCard">
          <Spinner animation="border" role="status">
            <span className="visually-hidden">Loading...</span>
          </Spinner>
          <p>Loading payroll ledger...</p>
        </div>
      ) : (
        <>
          <section className="incomeStatsGrid paySalariesStatsGrid">
            <article className="incomeStatCard incomeStatPrimary">
              <span className="incomeStatLabel">{getMonthLabel(periodKey)} payroll</span>
              <h3>{formatKes(payrollSummary.expected)}</h3>
              <p>Total salary expected across employees in the selected payroll month.</p>
            </article>
            <article className="incomeStatCard">
              <span className="incomeStatLabel">Paid this period</span>
              <h3>{formatKes(payrollSummary.paid)}</h3>
              <p>Salary already recorded against the selected payroll month.</p>
            </article>
            <article className="incomeStatCard">
              <span className="incomeStatLabel">Carry-forward arrears</span>
              <h3>{formatKes(payrollSummary.previousOutstanding)}</h3>
              <p>Outstanding balances from months before {getMonthLabel(periodKey)}.</p>
            </article>
            <article className="incomeStatCard">
              <span className="incomeStatLabel">Total due now</span>
              <h3>{formatKes(payrollSummary.totalOutstanding)}</h3>
              <p>Current month balance plus older arrears waiting to be cleared.</p>
            </article>
          </section>

          <div className="searchContainer incomeToolbar paySalariesToolbar">
            <div className="paySalariesFilters">
              <Form.Control
                type="month"
                value={periodKey}
                onChange={(event) => setPeriodKey(event.target.value)}
                className="paySalariesControl"
              />
              <Form.Control
                type="text"
                value={searchTerm}
                onChange={(event) => setSearchTerm(event.target.value)}
                placeholder="Search employee, campus, role..."
                className="paySalariesControl paySalariesSearch"
              />
              <Form.Select
                value={statusFilter}
                onChange={(event) => setStatusFilter(event.target.value)}
                className="paySalariesControl"
              >
                <option value="All">All statuses</option>
                <option value="Active">Active</option>
                <option value="Inactive">Inactive</option>
                <option value="Archived">Archived</option>
              </Form.Select>
            </div>
            <div className="incomeToolbarMeta">
              <span className="incomeToolbarText">
                Click an employee name to open the employee profile
              </span>
            </div>
          </div>

          <article className="incomeTableCard paySalariesMainCard payrollLedgerTableOnly">
            <div className="incomeTableHeader">
              <div>
                <h6>Payroll balances</h6>
                <p>Track this month balance, old arrears, and total due for each employee.</p>
              </div>
            </div>

            {filteredRows.length === 0 ? (
              <div className="incomeStateCard incomeEmptyState paySalariesEmptyState">
                <h6>No employees match this view</h6>
                <p>Try another filter or add employees before reviewing payroll balances.</p>
              </div>
            ) : (
              <div className="table-responsive">
                <table className="table align-middle incomeRecordsTable paySalariesTable">
                  <thead>
                    <tr>
                      <th>Employee</th>
                      <th>Month</th>
                      <th>This month</th>
                      <th>Previous balance</th>
                      <th>Total due now</th>
                      <th>Payroll status</th>
                      <th>Last payment</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredRows.map((employee) => (
                      <tr key={employee.id}>
                        <td>
                          <div className="paySalariesEmployeeCell">
                            <button
                              type="button"
                              className="paySalariesEmployeeLink"
                              onClick={() => navigate(`/view-employee/${employee.id}`)}
                            >
                              {employee.employeeName || 'Employee'}
                            </button>
                            <span>{employee.jobPosition || 'Role not set'} • {employee.campus || 'Campus not set'}</span>
                          </div>
                        </td>
                        <td>
                          <button
                            type="button"
                            className="payrollLedgerMonthBtn"
                            onClick={() => navigate(`/view-employee/${employee.id}`)}
                          >
                            {employee.currentBucket.periodLabel}
                          </button>
                        </td>
                        <td><span className="paySalariesAmountText">{formatKes(employee.currentBucket.remainingAmount)}</span></td>
                        <td><span className="paySalariesAmountText">{formatKes(employee.previousOutstanding)}</span></td>
                        <td>
                          <span className="paySalariesAmountText">{formatKes(employee.totalOutstanding)}</span>
                          <small className="paySalariesCellMeta">{employee.outstandingPeriods} unpaid month{employee.outstandingPeriods === 1 ? '' : 's'}</small>
                        </td>
                        <td>
                          <span className={`opsStatusPill opsStatusPill--${employee.paymentTone}`}>{employee.paymentStatus}</span>
                        </td>
                        <td>{formatDateTime(employee.lastPaymentDate)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </article>
        </>
      )}
    </div>
  );
}

export default PayrollLedgerComp;
